import React from "react";
import { Box, Typography, Button, Container, Grid } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CelebrationIcon from "@mui/icons-material/Celebration";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PeopleIcon from "@mui/icons-material/People";

const Hero = () => {
  // Smooth scroll to element by id
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        background: `
          radial-gradient(circle at top left, rgba(24,182,165,0.45), transparent 55%),
          radial-gradient(circle at bottom right, rgba(122,90,248,0.01), transparent 55%),
          linear-gradient(rgba(255,255,255,0.72), rgba(255,255,255,0.02)),
          url('https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1920&q=80')
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          {/* LEFT CONTENT */}
          <Grid item xs={12} md={7}>
            <Typography
              sx={{
                fontSize: { xs: "2.5rem", md: "3.8rem" },
                fontWeight: 900,
                lineHeight: 1.1,
                mb: 3,
                color: "#1E1E1E",
                pt: { xs: 4, md: 0 }, // Adds padding-top on mobile, removes on desktop
              }}
            >
              An Event{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #259386ff, #7A5AF8)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Is Not a Setup
              </Box>
              <br />
              It’s a Feeling
            </Typography>

            <Typography
              sx={{
                maxWidth: 520,
                fontSize: "1.1rem",
                color: "#5d4e9cff",
                mb: 5,
                pt: { xs: 2, md: 0 }, // Optional: adds padding to the second typography on mobile
              }}
            >
              We design celebrations around people, not decorations — creating
              experiences that stay long after the lights fade.
            </Typography>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 6 }}>
              {/* PLAN YOUR EVENT → scroll to contact form */}
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() => scrollToSection("contact")}
                sx={{
                  px: 4,
                  py: 1.6,
                  borderRadius: "30px",
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #18B6A5, #7A5AF8)",
                  boxShadow: "0 12px 30px rgba(24,182,165,0.35)",
                }}
              >
                Plan Your Event
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                height: { xs: 280, md: 340 }, // Increased from 220/260 to 280/340
                py: 4, // Added vertical padding
                borderRadius: "32px", // Increased from 20px to 32px for more prominence
                background: "rgba(255,255,255,0.85)", // Slightly more opaque
                backdropFilter: "blur(14px)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                px: 4, // Restored to original px=4
                boxShadow: "0 20px 40px rgba(0,0,0,0.08)", // Added subtle shadow for depth
              }}
            >
              <CelebrationIcon
                sx={{ fontSize: { xs: 70, md: 90 }, color: "#7A5AF8", mb: 2 }}
              />
              {/* Responsive icon size: 70 on mobile, 90 on desktop */}

              <Typography
                sx={{
                  fontSize: { xs: "1.5rem", md: "1.8rem" },
                  fontWeight: 800,
                  mb: 1,
                }}
              >
                {/* Responsive text: 1.5rem mobile, 1.8rem desktop */}
                Celebrate Without Stress
              </Typography>

              <Typography
                sx={{
                  color: "#555",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  maxWidth: "280px",
                }}
              >
                {/* Responsive text and max-width for better readability */}
                We manage the details — you enjoy the moments.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// Stat component (unchanged)
const Stat = ({ icon, value, label }) => (
  <Box
    sx={{
      minWidth: 170,
      p: 2.5,
      borderRadius: "20px",
      background: "rgba(255,255,255,0.75)",
      border: "1px solid rgba(255,255,255,0.6)",
      boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
      transition: "all .3s ease",
      "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: "0 20px 40px rgba(24,182,165,0.25)",
      },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        mb: 1.5,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #18B6A5, #7A5AF8)",
        color: "#fff",
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontWeight: 900, fontSize: "1.3rem" }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 14, color: "#666" }}>{label}</Typography>
  </Box>
);

export default Hero;
